import { useCallback, useEffect, useState } from "react";
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from "@mui/material";
import { useUser } from "../../../hooks/useUser";
import { formatCurrency, formatDateTime } from "../../../hooks/formatNumber";
import { PaymentHistory } from "../../../types/paymentHistory";
import LoadingOverlay from "../LoadingOverlay";

const rowsPerPage = 5;

type PaymentHistoryTableType = {
    history: PaymentHistory | null;
    setHistory: (v: PaymentHistory | null) => void;
    queryHistory: (id: number, pageIndex: number) => Promise<PaymentHistory | null>;
}

const PaymentHistoryTable = ({ history, setHistory, queryHistory }: PaymentHistoryTableType) => {
    const user = useUser();
    const [isLoading, setIsLoading] = useState(false);

    const [page, setPage] = useState(0);
    const pageData = useCallback(() => {
        return history?.records.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
    }, [page, history?.records]);

    const fetchData = useCallback(async () => {
        if (history === null) {
            setIsLoading(true);
            const response = await queryHistory(user.id, 1);
            setIsLoading(false);
            setHistory(response);
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handlePageChange = async (_event: any, newPage: number) => {
        if (history) {
            if (history.records.length <= newPage * rowsPerPage) {
                const response = await queryHistory(user.id, history.page + 1);
                history.addMore(response?.records ?? []);
            }
            setPage(newPage);
        }
    }
    
    return (
        <>
            <LoadingOverlay open={isLoading} />
            <TableContainer component={Paper} sx={{ overflow: 'hidden' }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <Typography variant="body2">Amount</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2">Tax</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2">Invoice</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2">Status</Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant="body2">Date Created</Typography>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {history?.totalRecords === 0 ? (
                            <TableRow key={0} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                <TableCell component="th" scope="row">
                                    <Typography>No payments have been made</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            pageData()?.map((payment) =>
                                <TableRow
                                    key={payment.created}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                    <TableCell>
                                        <Typography>
                                            {formatCurrency(payment.amount / 100, payment.currency, 'auto')}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography>
                                            {formatCurrency(payment.tax / 100, payment.currency, 'auto')}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {payment.invoiceNumber !== null ? (
                                            <a
                                                href={payment.invoiceUrl === "" ? undefined : payment.invoiceUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                <Typography>
                                                    {payment.invoiceNumber}
                                                </Typography>
                                            </a>
                                        ) : (
                                            <Typography>
                                                No Invoice Found
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography textTransform='capitalize'>
                                            {payment.status}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography>{formatDateTime(payment.created)}</Typography>
                                    </TableCell>
                                </TableRow>
                            )
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={history?.totalRecords ?? 0}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[]}
                page={page}
                onPageChange={handlePageChange}
            />
        </>
    )
}

export default PaymentHistoryTable;
